import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useComingSoonModal from "../../hooks/useComingSoonModal";
import { LAUNCH_SPORTS, HIDDEN_SPORTS, SPORT_META } from "../../config/sportsConfig";

// ─── Sports data is now managed centrally in src/config/sportsConfig.js ───────
//
// To add or reorder sports, edit LAUNCH_SPORTS in sportsConfig.js only.
// This component will reflect the change automatically — no edits needed here.

// ──────────────────────────────────────────────────────────────────────────────

const hiddenLower = HIDDEN_SPORTS.map((s) => s.toLowerCase());

const SportsBanner = ({ activeSport = null }) => {
  const url = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { openModal, Modal } = useComingSoonModal();

  const [sportsBanner, setSportsBanner] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);

  useEffect(() => {
    fetchBanner();
  }, []);

  // ── Sports strip ─────────────────────────────────────────────────────────────

  const fetchBanner = async () => {
    try {
      const { data } = await axios.get(
        `${url}/second_landing_page/get_sports_banner`
      );
      console.log("Sports Banner API", data);

      // PHP backend returns capital-D "Data"; production API returns lowercase "data".
      let sportsRes =
        data?.Data?.sportsBanner ||
        data?.data?.sportsBanner ||
        [];

      // Remove legacy sport_id === 0 placeholder
      sportsRes = sportsRes.filter((s) => String(s.sport_id) !== "0");

      // Remove hidden sports
      sportsRes = sportsRes.filter(
        (s) => !hiddenLower.includes((s.name || "").toLowerCase())
      );

      // Build lookup by API name
      const apiByName = {};
      sportsRes.forEach((s) => { apiByName[s.name] = s; });

      // Ordered launch sports — use API entry if present, else coming-soon stub
      const ordered = LAUNCH_SPORTS.map((name) =>
        apiByName[name] ?? { name, sport_id: null, link: "" }
      );

      // Append any API sports not in LAUNCH_SPORTS (future CRM additions)
      const launchSet = new Set(LAUNCH_SPORTS);
      const extras = sportsRes.filter((s) => !launchSet.has(s.name));

      setSportsBanner([
        ...ordered,
        ...extras,
        { name: "More Sports", sport_id: null, link: "" },
      ]);
    } catch (err) {
      console.error("[SportsBanner] fetch failed:", err?.response?.status, err?.message);
      setSportsBanner([]);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleSportClick = async (sport) => {
    // Only "More Sports" opens the modal — every other sport fetches leagues.
    if (sport.name === "More Sports") {
      openModal();
      return;
    }

    // KHAN-FE-019: fetch leagues first; if only one exists skip the inline
    // panel and navigate straight to the competition list.
    setLeaguesLoading(true);
    setSelectedSport(sport);
    setLeagues([]);
    try {
      const session_key = localStorage.getItem("session_key");
      const { data } = await axios.post(
        `${url}/api/second_landing_page/get_leagues`,
        { sport_id: sport.sport_id },
        { headers: { session_key } }
      );
      const content =
        data?.data?.content ||
        data?.Data?.content ||
        [];

      if (content.length === 1) {
        // Single league — go directly to the competition list, no panel shown.
        navigate(`/sports_leagues/${sport.sport_id}/${content[0].league_id}`);
        return;
      }

      setLeagues(content);
    } catch (err) {
      console.error("[SportsBanner] leagues fetch failed:", err?.response?.status, err?.message);
      setLeagues([]);
    } finally {
      setLeaguesLoading(false);
    }
  };

  const handleLeagueClick = (league) => {
    navigate(`/sports_leagues/${selectedSport.sport_id}/${league.league_id}`);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Sport strip — bullet-separated */}
      <div className="py-1 px-3 d-flex justify-content-center m-auto text-center primary-background">
        <div className="d-flex align-items-center flex-wrap justify-content-center">
          {sportsBanner.map((sport, index) => (
            <React.Fragment key={sport.sport_id ?? sport.name}>
              <span
                className="display_name_text"
                onClick={() => handleSportClick(sport)}
              >
                {/* Show friendly displayName (e.g. "NFL") even when the API
                    returns a different internal name (e.g. "American Football") */}
                {SPORT_META[sport.name]?.displayName ?? sport.name}
              </span>
              {index !== sportsBanner.length - 1 && (
                <span
                  className="mx-2"
                  style={{ color: "#9a9a9a", userSelect: "none" }}
                >
                  •
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Inline league panel — renders below strip on sport click */}
      {selectedSport && (
        <div className="page_content_center p-4">
          {leaguesLoading && (
            <p className="text-center">Loading...</p>
          )}
          {!leaguesLoading && leagues.length === 0 && (
            <h1 className="text-center coming_soon_text fs-1">
              Competitions Coming Soon...
            </h1>
          )}
          {!leaguesLoading && leagues.length > 0 && (
            <div className="p-3">
              {leagues.map((league) => (
                <h1
                  key={league.league_id}
                  className="display_name_text fs-5 py-1"
                  onClick={() => handleLeagueClick(league)}
                >
                  {league.league_name === "SERIE A" &&
                  league.league_country === "BRAZIL"
                    ? `BRAZIL ${league.league_name}`
                    : league.league_name}
                </h1>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal />
    </>
  );
};

export default SportsBanner;
